/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {signal} from '@preact/signals-react';
import {createMutator} from '@ion/applications/src/store/SignalUtils';
import {dlsLayout} from '../../theme/theme';

/**
 * Tracks whether the current-theme side navigation is collapsed.
 *
 * Components that dock next to the side nav (Global Group Chooser,
 * Notification panel, Profile panel) live outside its component tree and
 * need this value to stay in sync with live toggling. `useExpandCollapse`
 * cannot serve that need here - its state is backed by a per-hook-instance
 * `useState` seeded once from a persisted preference, so separate call
 * sites never see each other's updates.
 */
export const isSideNavCollapsed = signal<boolean>(false);

/**
 * Mutator function for updating the current-theme side navigation's
 * collapsed state.
 */
export const setIsSideNavCollapsed = createMutator(isSideNavCollapsed);

/**
 * Returns the pixel width the current-theme side nav occupies, collapsed or
 * expanded, for a panel that docks beside it.
 *
 * ```typescript
 *
 *   import {dlsSidebarWidth, isSideNavCollapsed} from '@ion/dls/src/components/navigation/NavigationStore';
 *   ...
 *
 *   const width = dlsSidebarWidth(isSideNavCollapsed.value);
 *
 * ```
 *
 * @param   collapsed Whether the side nav is currently collapsed.
 * @returns The matching sidebar width in pixels.
 */
export const dlsSidebarWidth = (collapsed: boolean): number => (
  dlsLayout(collapsed ? 'collapsedSidebar' : 'sidebar', 'width')
);

/**
 * Returns the CSS `left` offset, with `!important`, for a panel that docks
 * beside the current-theme side nav - matching whichever pixel width token
 * the nav currently occupies, collapsed or expanded.
 *
 * ```typescript
 *
 *   import {dlsSidebarOffset, isSideNavCollapsed} from '@ion/dls/src/components/navigation/NavigationStore';
 *   ...
 *
 *   const left = dlsSidebarOffset(isSideNavCollapsed.value);
 *
 * ```
 *
 * @param   collapsed Whether the side nav is currently collapsed.
 * @returns The matching sidebar width in pixels, as a CSS `!important`
 *          string.
 */
export const dlsSidebarOffset = (collapsed: boolean): string =>
  `${dlsSidebarWidth(collapsed)}px !important`;

// the side nav's edge line - a drawer that opens beside the nav starts just
// past it, so the line between the two stays in view ...
const SIDE_NAV_EDGE_LINE_WIDTH = 1;

/**
 * Returns the `left` offset, in pixels, for a drawer that opens beside the
 * current-theme side nav (e.g. the Global Group Chooser, Notifications or the
 * Tracking Filter) - the nav's current width plus its edge line, so the line
 * between the nav and the drawer stays visible.
 *
 * ```typescript
 *
 *   import {dlsSidebarDrawerLeft, isSideNavCollapsed} from '@ion/dls/src/components/navigation/NavigationStore';
 *   ...
 *
 *   const left = dlsSidebarDrawerLeft(isSideNavCollapsed.value);
 *
 * ```
 *
 * @param   collapsed Whether the side nav is currently collapsed.
 * @returns The drawer's left offset in pixels.
 */
export const dlsSidebarDrawerLeft = (collapsed: boolean): number => (
  dlsSidebarWidth(collapsed) + SIDE_NAV_EDGE_LINE_WIDTH
);

/**
 * Returns the CSS `left` offset, with `!important`, for a drawer that opens
 * beside the current-theme side nav - see `dlsSidebarDrawerLeft`.
 *
 * ```typescript
 *
 *   import {dlsSidebarDrawerOffset, isSideNavCollapsed} from '@ion/dls/src/components/navigation/NavigationStore';
 *   ...
 *
 *   const left = dlsSidebarDrawerOffset(isSideNavCollapsed.value);
 *
 * ```
 *
 * @param   collapsed Whether the side nav is currently collapsed.
 * @returns The drawer's left offset, as a CSS `!important` string.
 */
export const dlsSidebarDrawerOffset = (collapsed: boolean): string =>
  `${dlsSidebarDrawerLeft(collapsed)}px !important`;

/**
 * JSS `width`/`left` function-values for an element that should dock flush
 * against the current-theme side nav's actual pixel width (collapsed vs
 * expanded) and span the rest of the viewport, rather than a fixed offset
 * sized for the legacy nav. Shared by every current-theme desktop portal
 * container that docks beside the side nav (e.g. the persistent SDK iframe,
 * the ELD portal).
 *
 * ```typescript
 * import {dlsSidebarDockedStyle} from '@ion/dls/src/components/navigation/NavigationStore';
 * ...
 *
 * const useStyles = makeStyles(() => ({
 *   currentContainer : {
 *     ...dlsSidebarDockedStyle,
 *     height : '100vh !important',
 *     top    : '0px !important'
 *   }
 * }));
 * ```
 */
export const dlsSidebarDockedStyle = {
  width : ({collapsed}: {collapsed: boolean}) =>
    `calc(100vw - ${dlsLayout(collapsed ? 'collapsedSidebar' : 'sidebar', 'width')}px) !important`,
  left : ({collapsed}: {collapsed: boolean}) => dlsSidebarOffset(collapsed)
};
